import React from "react";
import { Save, Plus, Trash2, ChevronDown, Loader2 } from "lucide-react";

// Template-bar — extracted from QuoteView.jsx's toolbar (iter-102
// refactor). Lets the user pick / save / save-as / delete a quote
// header template (logo + company info + valid days + tax settings).
// All state lives in QuoteView; this component is purely presentational.
//
// Props:
//   templates           array of {id, name}
//   templateId          currently-selected template id ("" = none)
//   templateName        controlled string for the "Template name" input
//   showPicker          boolean — picker dropdown open?
//   templatesLoaded     boolean — gates the Save button on first fetch
//   saving              boolean — true while a save is in flight
//   onTogglePicker      () => void
//   onPickTemplate      (template) => void
//   onTemplateNameChange (newName) => void
//   onSave              () => void  (Save / Save as)
//   onSaveAsNew         () => void
//   onDelete            () => void
export function TemplateBar({
    templates,
    templateId,
    templateName,
    showPicker,
    templatesLoaded,
    saving,
    onTogglePicker,
    onPickTemplate,
    onTemplateNameChange,
    onSave,
    onSaveAsNew,
    onDelete,
}) {
    return (
        <>
            <div className="relative">
                <button
                    type="button"
                    data-testid="quote-template-picker"
                    onClick={onTogglePicker}
                    className="flex items-center gap-2 px-3 py-1.5 text-xs rounded-sm bg-[#1C253C] border border-[#2A3655] text-slate-200 hover:text-white hover:bg-[#2A3655] min-w-[180px] justify-between"
                >
                    <span className="truncate">
                        {templateName || "No template"}
                        {templates.length > 0 && (
                            <span className="text-slate-500 ml-1">
                                ({templates.length})
                            </span>
                        )}
                    </span>
                    <ChevronDown className="w-3 h-3 shrink-0" />
                </button>
                {showPicker && (
                    <div
                        className="absolute z-40 top-full left-0 mt-1 min-w-[240px] max-h-[300px] overflow-y-auto bg-[#131A2F] border border-[#2A3655] rounded-sm shadow-xl"
                        data-testid="quote-template-picker-menu"
                    >
                        {templates.length === 0 ? (
                            <div className="px-3 py-2 text-xs text-slate-500">
                                No saved templates yet
                            </div>
                        ) : (
                            templates.map((t) => (
                                <button
                                    key={t.id}
                                    type="button"
                                    data-testid={`quote-template-option-${t.id}`}
                                    onClick={() => onPickTemplate(t)}
                                    className={`w-full text-left px-3 py-1.5 text-xs hover:bg-[#1C253C] transition-colors ${
                                        t.id === templateId
                                            ? "text-[#00A3FF] bg-[#1C253C]"
                                            : "text-slate-200"
                                    }`}
                                >
                                    {t.name}
                                </button>
                            ))
                        )}
                    </div>
                )}
            </div>
            <input
                type="text"
                value={templateName}
                onChange={(e) => onTemplateNameChange(e.target.value)}
                placeholder="Template name"
                data-testid="quote-template-name-input"
                className="px-2 py-1.5 text-xs rounded-sm bg-[#1C253C] border border-[#2A3655] text-white placeholder:text-slate-500 w-[140px]"
            />
            <button
                type="button"
                onClick={onSave}
                disabled={saving || !templatesLoaded}
                data-testid="quote-save-template"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm bg-[#007AFF] hover:bg-[#3395FF] text-white font-bold transition-colors disabled:opacity-50"
                title="Save logo, company info, valid days, and tax settings to the selected template"
            >
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                {saving
                    ? "Saving…"
                    : templateId
                        ? "Save template"
                        : "Save as template"}
            </button>
            {templateId && (
                <button
                    type="button"
                    onClick={onSaveAsNew}
                    data-testid="quote-save-as-new"
                    className="flex items-center gap-1.5 px-2 py-1.5 text-xs rounded-sm bg-[#1C253C] border border-[#2A3655] text-slate-200 hover:text-white hover:bg-[#2A3655]"
                    title="Save current values as a new template"
                >
                    <Plus className="w-3 h-3" />
                    New
                </button>
            )}
            {templateId && (
                <button
                    type="button"
                    onClick={onDelete}
                    data-testid="quote-delete-template"
                    aria-label="Delete template"
                    className="flex items-center gap-1 px-2 py-1.5 text-xs rounded-sm text-slate-400 hover:text-red-300 hover:bg-red-500/15 transition-colors"
                >
                    <Trash2 className="w-3 h-3" />
                </button>
            )}
        </>
    );
}
